import { useEffect, useState } from "react";
import { AddressPicker } from "components/addressPicker/addressPicker.tsx";
import { ChevronDown, ChevronUp, Wrench } from "assets/icons";
import { CounterField } from "components/counterField/counterField.tsx";
import { EditableTextareaComponent } from "components/index";
import { getIconForLabel } from "shared/utils/get-icons";
import { AvailableRenovationLabel } from "shared/types/renovation";
import { Toggle } from "components/toggle/toggle";
import { BuildingDTO } from "shared/types/building";

export type BuildingProps = {
  building: BuildingDTO;
  updateBuildingDetails: any;
};

export type AvailableRenovationLabels = {
  name: AvailableRenovationLabel;
  icon: JSX.Element | null;
};

const availableRenovatedLabels: AvailableRenovationLabels[] = Object.values(
  AvailableRenovationLabel,
)
  .map((label: any) => ({
    icon: getIconForLabel(label),
    name: label,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const Building = ({ building, updateBuildingDetails }: BuildingProps) => {
  const [openAdditionalDetails, setOpenAdditionalDetails] =
    useState<boolean>(false);
    const [selectedAddress, setSelectedAddress] = useState(building.location.description);

  useEffect(() => {
    setSelectedAddress(building.location.description);
  }, [building]);

  const handleDetailChange = (field: any, value: any) => {
    console.log("Handling details change");
    console.log(field);
    console.log(value);
    if(field == 'location') {
      setSelectedAddress(value.description);
    }
    updateBuildingDetails(building.tempId, field, value);
  };

  const handleToggleRenovated = (label: any, isActive: any) => {
    const updatedLabels = isActive
      ? [...(building.buildingAlreadyRenovated as any), label]
      : (building.buildingAlreadyRenovated as any).filter(
          (l: any) => l !== label,
        );
    // Include building ID in the update call
    updateBuildingDetails(
      building.tempId,
      "buildingAlreadyRenovated",
      updatedLabels,
    );
  };

  const handleToggleToRenovate = (label: any, isActive: any) => {
    const updatedLabels = isActive
      ? [...(building.buildingToRenovate as any), label]
      : (building.buildingToRenovate as any).filter((l: any) => l !== label);
    // Include building ID in the update call
    updateBuildingDetails(building.tempId, "buildingToRenovate", updatedLabels);
  };

  return (
    <div className="flex flex-col gap-4 rounded-md border px-3 py-5">
      <div className="flex items-center gap-2">
        <div className="w-full">
          <AddressPicker
            editMode={true}
            label="Start met het intikken van het adres..."
            defaultAddress={selectedAddress}
            getAddress={(value) => handleDetailChange("location", { description: value.description, placeId: value.placeId })}
          />
        </div>
      </div>
      <div
        className="flex items-center gap-2"
        onClick={() => setOpenAdditionalDetails(!openAdditionalDetails)}
      >
        <span className="w-fill h-px flex-1 bg-neutral-primary/20"></span>
        <div className="flex cursor-pointer items-center text-sm">
          {!openAdditionalDetails ? (
            <p>Toon extra gegevens</p>
          ) : (
            <p>Verberg extra gegevens</p>
          )}
          {!openAdditionalDetails ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </div>
        <span className="w-fill h-px flex-1 bg-neutral-primary/20"></span>
      </div>
      {openAdditionalDetails && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <p className="text-sm">Beschrijving van het gebouw</p>
            <EditableTextareaComponent
              editMode={true}
              isLoading={false}
              label="Extra informatie over het gebouw"
              value={
                building.buildingAcquisitionInfo
                  ?.acquisitionFileBuildingDescription as string
              }
              onChange={(value) =>
                handleDetailChange("acquisitionFileBuildingDescription", value)
              }
            />
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-nowrap">
            <CounterField
              label="Res. units"
              defaultCount={
                building.buildingAcquisitionInfo
                  ?.acquisitionFileNumResidentialUnits
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumResidentialUnits", count);
              }}
            />
            <CounterField
              label="Retail units"
              defaultCount={
                building.buildingAcquisitionInfo?.acquisitionFileNumRetailUnits
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumRetailUnits", count);
              }}
            />
            <CounterField
              label="Kantoor units"
              defaultCount={
                building.buildingAcquisitionInfo?.acquisitionFileNumOfficeUnits
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumOfficeUnits", count);
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2 md:flex-nowrap">
            <CounterField
              label="Parking binnen"
              defaultCount={
                building.buildingAcquisitionInfo
                  ?.acquisitionFileNumParkingsInside
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumParkingsInside", count);
              }}
            />
            <CounterField
              label="Parking buiten"
              defaultCount={
                building.buildingAcquisitionInfo
                  ?.acquisitionFileNumParkingsOutside
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumParkingsOutside", count);
              }}
            />
            <CounterField
              label="Fietsenstallingen"
              defaultCount={
                building.buildingAcquisitionInfo?.acquisitionFileNumBicycleSheds
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumBicycleSheds", count);
              }}
            />
            <CounterField
              label="Bergingen"
              defaultCount={
                building.buildingAcquisitionInfo?.acquisitionFileNumStorages
              }
              handleCountChange={(operation, count) => {
                console.log(operation);
                handleDetailChange("acquisitionFileNumStorages", count);
              }}
            />
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1 text-sm uppercase text-neutral-secondary-mid">
              <Wrench className="h-[18px] w-[18px]" /> Reeds Gerenoveerd
            </div>
            <div className="flex flex-wrap gap-2">
              {availableRenovatedLabels.map((option) => (
                <Toggle
                  key={option.name}
                  label={option.name}
                  isActive={(building.buildingAlreadyRenovated as any).includes(
                    option.name,
                  )}
                  onClick={handleToggleRenovated}
                />
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-1 text-sm uppercase text-neutral-secondary-mid">
              <Wrench className="h-[18px] w-[18px]" /> Nog te renoveren
            </div>
            <div className="flex flex-wrap gap-2">
              {availableRenovatedLabels.map((option) => (
                <Toggle
                  key={option.name}
                  label={option.name}
                  isActive={(building.buildingToRenovate as any).includes(
                    option.name,
                  )}
                  onClick={handleToggleToRenovate}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Building;
